import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { proposalError } from "@/payroll/editor/state";
import type { Workspace } from "@/payroll/editor/types";

export const ProposalMenu = ({
  workspace,
  discard,
  busy,
}: {
  workspace: Workspace;
  discard: PayrollEditorQueries["discard"];
  busy: boolean;
}) => {
  const [confirming, setConfirming] = useState(false);
  if (!workspace.editable) return null;

  const reason = busy ? "Wait for the current change to finish" : "";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="More proposal actions"
            />
          }
        >
          <DotsThreeIcon data-icon="inline-start" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuItem
            disabled={!!reason || discard.isPending}
            onClick={() => {
              discard.reset();
              setConfirming(true);
            }}
          >
            <ArrowCounterClockwiseIcon className="h-4 w-4" />
            <Stack align="start" gap={0}>
              <Text variant="caption">Start over</Text>
              {!!reason && (
                <Text variant="caption" tone="muted">
                  {reason}
                </Text>
              )}
            </Stack>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start over?</DialogTitle>
            <DialogDescription>
              {workspace.has_changes ||
              workspace.proposal_retained ||
              workspace.has_roster_edits !== false ? (
                <>
                  This drops the proposed rules and this conversation, and puts
                  back anyone this proposal moved on the roster. It also removes
                  employees this proposal added. Roster changes somebody made by
                  hand stay as they are. Your published rules do not change, and
                  nobody's pay moves.
                </>
              ) : (
                "This will clear your chat thread."
              )}
            </DialogDescription>
          </DialogHeader>
          {!!discard.error && (
            <p role="alert" className="text-xs text-danger">
              {proposalError(discard.error)}
            </p>
          )}
          <DialogFooter className="sm:gap-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirming(false)}
            >
              Keep it
            </Button>
            <Button
              size="sm"
              loading={discard.isPending}
              onClick={() =>
                discard.mutate(undefined, {
                  onSuccess: () => setConfirming(false),
                })
              }
            >
              Start over
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
